import { createContext, useCallback, useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { StatusBanner } from '@xing-com/banner';

type ApplyErrorBannerContextValue = {
  showErrorBanner: (copyId: string) => void;
  clearErrorBanner: () => void;
};

const ApplyErrorBannerContext = createContext<
  ApplyErrorBannerContextValue | undefined
>(undefined);

export const ApplyErrorBannerContextProvider: React.FC<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const [isErrorBannerShown, setIsErrorBannerShown] = useState<boolean>(false);
  const [errorCopyId, setErrorCopyId] = useState<string>('');

  const showErrorBanner = useCallback((copyId: string) => {
    setErrorCopyId(copyId);
    if (copyId) setIsErrorBannerShown(true);
  }, []);
  const hideErrorBanner = useCallback(() => {
    setIsErrorBannerShown(false);
  }, []);

  return (
    <>
      <StatusBanner
        data-testid="apply-error-banner"
        display="fixed"
        handleOnClose={hideErrorBanner}
        show={isErrorBannerShown}
        variant="error"
      >
        <FormattedMessage
          id={errorCopyId}
          values={{
            b: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </StatusBanner>
      <ApplyErrorBannerContext.Provider
        value={{
          showErrorBanner: showErrorBanner,
          clearErrorBanner: hideErrorBanner,
        }}
      >
        {children}
      </ApplyErrorBannerContext.Provider>
    </>
  );
};

export const useApplyErrorBanner = (): ApplyErrorBannerContextValue => {
  const errorBannerContextValue = useContext(ApplyErrorBannerContext);

  if (errorBannerContextValue === undefined) {
    throw new Error(
      'useApplyErrorBanner must be used within an ApplyErrorBannerContext'
    );
  }

  return errorBannerContextValue;
};
